import type { GraphQLRequest } from "./hasuraHelpers"
import { addAuthToRequest } from "./hasuraHelpers"
import type { RequestMiddleware as GqlRequestMiddleware } from "graphql-request"
import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"
import AccessRules from "@/types/AccessRules"

interface BlockingResult {
  shouldBlock: boolean
  ruleName?: string
  reason?: string
}

class RequestMiddleware {
  private static instance: RequestMiddleware | null = null
  private accessRules: AccessRules[] = []

  public static getInstance() {
    this.instance ??= new RequestMiddleware()
    return this.instance
  }

  registerAccessRules(rules: AccessRules) {
    this.accessRules.push(rules)
  }

  clearAccessRules() {
    this.accessRules = []
  }

  createRestInterceptor() {
    return {
      request: (config: InternalAxiosRequestConfig) => {
        const blockingResult = this.shouldBlockRestRequest(config)

        if (blockingResult.shouldBlock) {
          const endpoint = this.getRestEndpoint(config)
          const method = config.method?.toUpperCase()

          console.warn("Request blocked by access control", {
            ruleName: blockingResult.ruleName,
            method,
            endpoint,
            url: config.url,
            reason: blockingResult.reason,
          })

          const error = new Error(blockingResult.reason)
          throw error
        }

        return config
      },

      response: (response: AxiosResponse) => response,

      responseError: (error: AxiosError) => {
        return Promise.reject(error)
      },
    }
  }

  createGraphQLRequestMiddleware(): GqlRequestMiddleware {
    return async (request) => {
      const enhancedRequest = await addAuthToRequest(request)

      const blockingResult = this.shouldBlockGraphQLRequest(enhancedRequest)

      if (blockingResult.shouldBlock) {
        const operationName = this.getGraphQLOperationName(enhancedRequest)

        console.warn("Request blocked by access control", {
          ruleName: blockingResult.ruleName,
          operationName,
          operationType: this.getGraphQLOperationType(enhancedRequest),
          reason: blockingResult.reason,
        })

        const error = new Error(blockingResult.reason)
        throw error
      }

      return enhancedRequest
    }
  }

  private shouldBlockRestRequest(config: AxiosRequestConfig): BlockingResult {
    for (const rules of this.accessRules) {
      if (rules.shouldApply()) {
        const method = config.method?.toUpperCase()

        if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
          continue
        }

        const endpoint = this.getRestEndpoint(config)
        const isAllowed = this.isEndpointAllowed(
          endpoint,
          rules.allowedRestEndpoints || [],
        )

        if (!isAllowed) {
          return {
            shouldBlock: true,
            ruleName: rules.name,
            reason: `Access denied: ${rules.name} cannot perform ${method} operation on '${endpoint}'`,
          }
        }
      }
    }

    return { shouldBlock: false }
  }

  private shouldBlockGraphQLRequest(request: GraphQLRequest): BlockingResult {
    for (const rules of this.accessRules) {
      if (rules.shouldApply()) {
        const operationType = this.getGraphQLOperationType(request)

        if (operationType === "query" || operationType === "subscription") {
          continue
        }

        if (operationType === "mutation") {
          const operationName = this.getGraphQLOperationName(request)
          const isAllowed = this.isMutationAllowed(
            operationName,
            rules.allowedGraphQLMutations || [],
          )

          if (!isAllowed) {
            return {
              shouldBlock: true,
              ruleName: rules.name,
              reason: `Access denied: ${rules.name} cannot perform mutation '${operationName}'`,
            }
          }
        }
      }
    }

    return { shouldBlock: false }
  }

  private isEndpointAllowed(
    endpoint: string,
    allowedEndpoints: string[],
  ): boolean {
    return allowedEndpoints.some((allowedPath) => {
      if (endpoint === allowedPath) return true

      if (allowedPath.includes("*")) {
        const pattern = allowedPath.replace(/\*/g, ".*")
        const regex = new RegExp(`^${pattern}$`)
        return regex.test(endpoint)
      }

      return endpoint.startsWith(allowedPath)
    })
  }

  private isMutationAllowed(
    operationName: string,
    allowedMutations: string[],
  ): boolean {
    return allowedMutations.some((allowedMutation) => {
      if (operationName === allowedMutation) return true

      if (allowedMutation.includes("*")) {
        const pattern = allowedMutation.replace(/\*/g, ".*")
        const regex = new RegExp(`^${pattern}$`, "i")
        return regex.test(operationName)
      }

      return operationName.toLowerCase().includes(allowedMutation.toLowerCase())
    })
  }

  private getRestEndpoint(config: AxiosRequestConfig) {
    let endpoint = config.url || ""

    if (config.baseURL && endpoint.startsWith(config.baseURL)) {
      endpoint = endpoint.substring(config.baseURL.length)
    }

    if (!endpoint.startsWith("/")) {
      endpoint = "/" + endpoint
    }

    const queryIndex = endpoint.indexOf("?")
    if (queryIndex !== -1) {
      endpoint = endpoint.substring(0, queryIndex)
    }

    return endpoint
  }

  private getGraphQLOperationName(request: GraphQLRequest) {
    if (request.operationName) {
      return request.operationName
    }

    return "unknown"
  }

  private getGraphQLOperationType(request: GraphQLRequest) {
    const body = JSON.parse(String(request.body))

    if (body.query) {
      const query = body.query

      if (typeof query === "string") {
        const operationRegex = /^\s*(query|mutation|subscription)\s+/i
        const operationMatch = operationRegex.exec(query)

        if (operationMatch) {
          return operationMatch[1].toLowerCase()
        }
      }
    }

    return "unknown"
  }
}

export const requestMiddleware = RequestMiddleware.getInstance()
